// src/services/articleService.ts

import type { Article } from '../types/article'

const API_BASE = process.env.REACT_APP_API_BASE_URL ?? ''

/* ─────────────────────────────────────────────────
   Types
───────────────────────────────────────────────── */

export interface ArticleListParams {
  /** Filtre par catégorie */
  category?: string
  /** Filtre par tag */
  tag?: string

  // PAGINATION DÉSACTIVÉE — décommenter pour réactiver
  // page?: number
  // pageSize?: number
}

/*
 * L'API retourne directement un tableau Article[]
 * sur GET /api/article (pas d'enveloppe { items, totalCount }).
 */
export type ArticleListResponse = Article[]

/* ─────────────────────────────────────────────────
   Service
───────────────────────────────────────────────── */

/**
 * Récupère tous les articles.
 * Endpoint : GET /api/article
 * Réponse  : Article[]
 */
export async function fetchArticles(
  _params: ArticleListParams = {}
): Promise<ArticleListResponse> {
  // Aucun query param envoyé — l'API ne prend pas de pagination
  const url = `${API_BASE}/api/article`

  console.debug('[articleService] fetchArticles → GET', url)

  const response = await fetch(url)

  console.debug('[articleService] fetchArticles ← status', response.status, response.statusText)

  if (!response.ok) {
    console.error('[articleService] fetchArticles — échec HTTP', {
      status: response.status,
      url,
    })
    throw new Error(`Erreur ${response.status} : impossible de charger les articles.`)
  }

  const data: ArticleListResponse = await response.json()

  console.debug('[articleService] fetchArticles — données reçues', {
    count: data.length,
    items: data,
  })

  return data
}

/**
 * Récupère tous les articles (y compris non publiés pour les admins).
 * Endpoint : GET /api/article/admin
 * Réponse  : Article[]
 */
export async function fetchAllArticles(
  token: string
): Promise<ArticleListResponse> {
  const url = `${API_BASE}/api/article/admin`

  console.debug('[articleService] fetchAllArticles → GET', url)

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  console.debug('[articleService] fetchAllArticles ← status', response.status, response.statusText)

  if (!response.ok) {
    console.error('[articleService] fetchAllArticles — échec HTTP', {
      status: response.status,
      url,
    })
    throw new Error(`Erreur ${response.status} : impossible de charger les articles.`)
  }

  const data: ArticleListResponse = await response.json()

  console.debug('[articleService] fetchAllArticles — données reçues', {
    count: data.length,
    items: data,
  })

  return data
}

/**
 * Récupère le détail d'un article par son UUID.
 * Endpoint : GET /api/article/{id}
 * Réponse  : Article
 *
 * Le slug n'est pas envoyé à l'API — il sert uniquement
 * à construire une URL lisible côté front (/blog/mon-article-<id>).
 */
export async function fetchArticleById(id: string): Promise<Article> {
  const url = `${API_BASE}/api/article/${id}`

  console.debug('[articleService] fetchArticleById → GET', url, { id })

  const response = await fetch(url)

  console.debug('[articleService] fetchArticleById ← status', response.status, response.statusText)

  if (!response.ok) {
    console.error('[articleService] fetchArticleById — échec HTTP', {
      status: response.status,
      id,
      url,
    })
    if (response.status === 404) {
      throw new Error('Article introuvable.')
    }
    throw new Error(`Erreur ${response.status} : impossible de charger l'article.`)
  }

  const article: Article = await response.json()

  console.debug('[articleService] fetchArticleById — données reçues', {
    id: article.id,
    title: article.title,
  })

  return article
}

/**
 * Récupère le détail d'un article par son slug.
 * Endpoint : GET /api/article/slug/{slug}
 * Réponse  : Article
 */
export async function fetchArticleBySlug(slug: string): Promise<Article> {
  const url = `${API_BASE}/api/article/slug/${slug}`

  console.debug('[articleService] fetchArticleBySlug → GET', url, { slug })

  const response = await fetch(url)

  console.debug('[articleService] fetchArticleBySlug ← status', response.status, response.statusText)

  if (!response.ok) {
    console.error('[articleService] fetchArticleBySlug — échec HTTP', {
      status: response.status,
      slug,
      url,
    })
    if (response.status === 404) {
      throw new Error('Article introuvable.')
    }
    throw new Error(`Erreur ${response.status} : impossible de charger l'article.`)
  }

  const article: Article = await response.json()

  console.debug('[articleService] fetchArticleBySlug — données reçues', {
    id: article.id,
    title: article.title,
    slug: article.slug,
  })

  return article
}

/* ─────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────── */

/** Crée une Error avec le code HTTP attaché en propriété `status`. */
function httpError(status: number, message: string): Error {
  const err = new Error(message)
  ;(err as any).status = status
  return err
}

/* ─────────────────────────────────────────────────
   Creation
───────────────────────────────────────────────── */

/**
 * Informations nécessaires pour créer un article. Les champs sont
 * en camelCase mais seront convertis en PascalCase côté API.
 */
export interface NewArticlePayload {
  title: string
  description: string
  content: string
  cover?: File
  categories: string[]
  tags: string[]
}

/**
 * Informations nécessaires pour mettre à jour un article. Les champs sont
 * en camelCase mais seront convertis en PascalCase côté API.
 */
export interface UpdateArticlePayload {
  title: string
  description: string
  content: string
  cover?: string
  newCoverFile?: File
  author: string
  isPublished: boolean
  categories: string[]
  tags: string[]
}

/**
 * Crée un article (multipart/form-data) via POST /api/article.
 */
export async function createArticle(
  token: string,
  payload: NewArticlePayload
): Promise<Article> {
  const url = `${API_BASE}/api/article`
  console.debug('[articleService] createArticle → POST', url, payload)

  const form = new FormData()
  form.append('Title', payload.title)
  form.append('Description', payload.description)
  form.append('Content', payload.content)
  if (payload.cover) {
    form.append('Cover', payload.cover)
  }
  payload.categories.forEach((c) => form.append('Categories', c))
  payload.tags.forEach((t) => form.append('Tags', t))

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: form,
  })

  console.debug('[articleService] createArticle ← status', response.status)

  if (!response.ok) {
    let message = 'Erreur lors de la création de l\'article.'
    try {
      const data = await response.json()
      if (data?.error) message = data.error
    } catch {
      /* ignore */
    }
    throw httpError(response.status, message)
  }

  const article: Article = await response.json()
  console.debug('[articleService] createArticle ← article', article)
  return article
}

/**
 * Met à jour un article (multipart/form-data) via PATCH /api/article/{id}.
 */
export async function updateArticle(
  token: string,
  articleId: string,
  payload: UpdateArticlePayload
): Promise<Article> {
  const url = `${API_BASE}/api/article/${articleId}`
  console.debug('[articleService] updateArticle → PATCH', url, payload)

  const form = new FormData()
  form.append('Id', articleId)
  form.append('Title', payload.title)
  form.append('Description', payload.description)
  form.append('Content', payload.content)
  form.append('Author', payload.author)
  form.append('IsPublished', String(payload.isPublished))
  if (payload.cover) form.append('Cover', payload.cover)
  if (payload.newCoverFile) form.append('NewCoverFile', payload.newCoverFile)
  payload.categories.forEach((c) => form.append('Categories', c))
  payload.tags.forEach((t) => form.append('Tags', t))

  const response = await fetch(url, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: form,
  })

  console.debug('[articleService] updateArticle ← status', response.status)

  if (!response.ok) {
    let message = 'Erreur lors de la mise à jour de l\'article.'
    try {
      const data = await response.json()
      if (data?.error) message = data.error
    } catch {
      /* ignore */
    }
    throw new Error(message)
  }

  const article: Article = await response.json()
  console.debug('[articleService] updateArticle ← article', article)
  return article
}

/**
 * Bascule le statut de publication d'un article via PATCH /api/article/{id}/publish.
 */
export async function togglePublishArticle(
  token: string,
  articleId: string,
  isPublished: boolean
): Promise<Article> {
  const url = `${API_BASE}/api/article/${articleId}/publish`
  console.debug('[articleService] togglePublishArticle → PATCH', url, { isPublished })

  const response = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ isPublished }),
  })

  console.debug('[articleService] togglePublishArticle ← status', response.status)

  if (!response.ok) {
    let message = 'Erreur lors de la mise à jour du statut de publication.'
    try {
      const data = await response.json()
      if (data?.error) message = data.error
    } catch {
      /* ignore */
    }
    throw new Error(message)
  }

  const article: Article = await response.json()
  console.debug('[articleService] togglePublishArticle ← article', article)
  return article
}

/**
 * Supprime un article via DELETE /api/article/{id}.
 */
export async function deleteArticle(
  token: string,
  articleId: string
): Promise<void> {
  const url = `${API_BASE}/api/article/${articleId}`
  console.debug('[articleService] deleteArticle → DELETE', url)

  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  console.debug('[articleService] deleteArticle ← status', response.status)

  if (!response.ok) {
    let message = 'Erreur lors de la suppression de l\'article.'
    try {
      const data = await response.json()
      if (data?.error) message = data.error
    } catch {
      /* ignore */
    }
    throw new Error(message)
  }

  console.debug('[articleService] deleteArticle — succès')
}